import "./RegisterPage.scss";
import React, { useCallback, useEffect, useState } from "react";
import RegStepOne, { RestaurantType } from "./RegStepOne";
import RegStepTwo from "./RegStepTwo";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { env } from "../../env/env";
import RegStepFour from "./RegStepFour";
import { useParams } from "react-router";
import { RegMemberDto } from "../../generated/dashboard-client";
import { logSignIn, logSignUp } from "../../service/AnalyticsService";
import { iniFrame, setSiteBannerCookie } from "../../util/utils";
import MembersApi from "../../api/MembersApi";
import RegStepZero, { RegOrLoginDecision } from "./RegStepZero";
import LoginPageStep from "./LoginPageStep";
import Storage from "../../util/Storage";
import AppApi from "../../api/AppApi";
import { handleServerError } from "../../util/ErrorHandler";
import RegStepTrial from "./RegStepTrial";
import RegStepPopmenuAuth from "./RegStepPopmenuAuth";
import RegStepPopmenuLocation from "./RegStepPopmenuLocation";
import { RegPageNavProps } from "../../util/types";
import DashboardApi from "../../api/DashboardApi";

enum RegStep {
  Login, LoginUser, LoginPassword, Use, Fields, Confirm, TrialSetup, PopmenuAuth, PopmenuLocation
}
interface LoginProps {
  onLogin?: () => void;
}
export default function RegisterPage({ onLogin }: LoginProps) {
  const { inviteId } = useParams<{ inviteId: string }>();

  const navigate = useNavigate();
  const [step, setStep] = useState<RegStep>(RegStep.Login);
  const [type, setType] = useState<RestaurantType>(RestaurantType.Other);
  const [next, setNext] = useState(null as string | null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [decision, setDecision] = useState({} as RegOrLoginDecision);
  const location = useLocation();
  const state = location.state || {};
  const [searchParams] = useSearchParams();
  const [popmenuFlow, setPopmenuFlow] = useState(false);

  useEffect(() => {
    if (!searchParams) return;
    setPopmenuFlow(searchParams.get("source") === 'popmenu');
  }, [searchParams]);

  useEffect(() => {
    if (!state.email) return;
    setEmail(state.email);
  }, [state.email]);

  useEffect(() => {
    if (!state.step) return;
    if (state.step === 'pass') {
      setStep(RegStep.LoginPassword);
    }
  }, [state.step]);

  useEffect(() => {
    if (Storage.getToken()) {
      if ((location.state as RegPageNavProps)?.resumePopmenuFlow) {
        console.log('=> resume popmenu flow');
        setPopmenuFlow(true);
      } else {
        onLogin?.();
        navigate(env.dashboardPath);
      }
    }
  }, [navigate, onLogin, location.state]);

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    setNext(query.get("next") ?? null);
  }, []);

  useEffect(() => {
    if (state?.step || !location) return;

    if ((location.state as RegPageNavProps)?.resumePopmenuFlow) {
      setStep(RegStep.PopmenuLocation);
    } else {
      setStep(inviteId ? RegStep.Fields : RegStep.Login);
    }
  }, [inviteId, state.step, location]);

  function onTypeSelect(type: RestaurantType) {
    setType(type);
    setStep(RegStep.Fields);
  }

  function onSignIn() {
    setStep(RegStep.LoginUser);
  }

  function toDashboard() {
    if (next) {
      if (next === 'upgrade') {
        navigate(env.dashboardPath, { state: { showUpgradeModal: true } });
      } else {
        window.location.href = next;
      }
    } else {
      if (popmenuFlow) {
        window.location.href = env.dashboardPath + '?popmenuWelcome=true';
      } else {
        navigate(env.dashboardPath);
      }
    }
  }

  function onRegSuccess(member: RegMemberDto, internalId: number = 0) {
    if (!inviteId) {
      logSignUp(internalId, type);

      if (type === RestaurantType.Multiple) {
        MembersApi.sendMultiRegLead(member);
      }

      if (iniFrame()) {
        window.location.href = `${env.basePath}${env.timberContextPath}/?size=digital-menu-board`;
      } else {
        if (popmenuFlow) {
          setStep(RegStep.TrialSetup);
        } else {
          setStep(RegStep.Confirm);
        }
      }
      return;
    }

    if (next) {
      if (next === 'upgrade') {
        navigate(env.dashboardPath, { state: { showUpgradeModal: true } });
      } else {
        window.location.href = next;
      }
    } else {
      navigate(env.dashboardPath);
    }
  }

  function onDecision(decision: RegOrLoginDecision) {
    setDecision(decision);
    if (decision.type === 'google') {
      if (decision.gMember?.id) {
        setLoading(true);
        setEmail(decision.gMember.email!);
        setPassword(decision.gMember.token!);
        performLogin(decision.gMember?.email!, decision.gMember?.token!).then();
      } else {
        setStep(RegStep.Use);
      }
    } else {
      setEmail('');
      setPassword('');
      setError('');
      setStep(RegStep.LoginUser);
    }
  }

  const performLogin = useCallback(async (email: string, password: string) => {
    if (!password || !email) return;
    setLoading(true);
    try {
      const loginResp = await AppApi.login({ username: email, password: password.trim() });
      const resp = loginResp.data;

      if (resp.error) {
        let errMsg: any = "We couldn't find that login / password combination.";
        if ('paused' === resp.code) {
          errMsg = "This account is paused. Please contact your account owner.";
        } else if ('canceled' === resp.code) {
          errMsg = "This account is cancelled. Please contact your account owner.";
        } else if ('declined' === resp.code) {
          errMsg = "This account is declined. Please contact your account owner.";
        } else if ('blocked' === resp.code) {
          errMsg = <div style={{ textAlign: "center" }}>
            <b>This account is blocked.</b><br />
            Please contact our customer service team.
          </div>;
        }
        setError(errMsg);
        setLoading(false);
        return;
      }

      if (resp.token) {
        Storage.setToken(resp.token);
      }

      logSignIn(resp.mid + '');
      setSiteBannerCookie(true);
      try {
        await MembersApi.increaseMemberLoginCount(resp.mid!);
      } catch (e) {
        console.error(e);
      }

      if (next) {
        window.location.href = next;
      } else {
        if (!popmenuFlow) {
          onLogin?.();
        }

        if (iniFrame()) {
          // if in iFrame go to Timber
          window.location.href = `${env.basePath}${env.timberContextPath}/?size=digital-menu-board`;
        } else {
          if (popmenuFlow) {
            setLoading(true);
            DashboardApi.status().then((resp) => {
              const { member } = resp.data;
              setLoading(false);
              if (member?.status === 5) {
                setStep(RegStep.TrialSetup);
              } else if (member?.status === 1) {
                setStep(RegStep.PopmenuAuth);
              }
            });
          } else {
            navigate(env.dashboardPath);
          }
        }
      }

    } catch (err) {
      console.error(err);
      setError("We couldn't find that login / password combination.");
    }

    setLoading(false);
  }, [navigate, next, onLogin, popmenuFlow]);

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    let toBeNext = null;
    if (query.get("next") !== null) {
      if (query.get("next")!.indexOf('pos-verification') > -1) {
        toBeNext = window.location.href.split('next=')[1];
      } else {
        toBeNext = query.get("next");
      }
    }
    setNext(toBeNext);

    if (query.get("tkn")) {
      Storage.setToken(query.get("tkn")!);
      setTimeout(() => navigate(env.dashboardPath), 3000)
    }

    if (query.get("e") && query.get("p")) {
      setEmail(query.get("e")!);
      setPassword(query.get("p")!);
      performLogin(query.get("e")!, query.get("p")!).then();
    }
  }, [navigate, performLogin])

  function onLoginUserNext() {
    setPassword('');
    setError('');

    setLoading(true);
    AppApi.checkEmail(email).then(resp => {
      setLoading(false);
      if (resp.data) {
        setStep(RegStep.LoginPassword);
      } else {
        setStep(RegStep.Use);
      }
    });
  }

  function onLoginPasswordNext() {
    setLoading(true);
    AppApi.checkEmail(email).then(resp => {
      if (resp.data) {
        performLogin(email, password).then();
      } else {
        setLoading(false);
        setStep(RegStep.Use);
      }
    }).catch(e => {
      handleServerError(e);
      setLoading(false);
    });
  }

  function popmenuNext() {
    if (step === RegStep.PopmenuLocation) {
      setStep(RegStep.Confirm);
    } else {
      setStep(RegStep.PopmenuAuth);
    }
  }

  return (
    <div className={`reg-page ${(step === RegStep.TrialSetup) ? 'trial' : ''}`}>
      <div className={'left'}>
      </div>
      <div className={'right'}>
        {step === RegStep.Login &&
          <RegStepZero onDecision={onDecision} loading={loading} onStartLoading={() => setLoading(true)} />
        }
        {step === RegStep.LoginUser &&
          <LoginPageStep step={'email'} email={email} loading={loading} error={error} password={password} onEmailChange={setEmail} onPasswordChange={setPassword} onNext={onLoginUserNext} onBack={() => setStep(RegStep.Login)} />
        }
        {step === RegStep.LoginPassword &&
          <LoginPageStep step={'password'} email={email} loading={loading} error={error} password={password} onEmailChange={setEmail} onPasswordChange={setPassword} onNext={onLoginPasswordNext} onBack={() => setStep(RegStep.LoginUser)} />
        }
        {step === RegStep.Use &&
          <RegStepOne
            onBack={() => setStep(RegStep.LoginUser)}
            onTypeSelect={onTypeSelect}
            onSignIn={onSignIn}
          />}
        {step === RegStep.Fields &&
          <RegStepTwo
            type={type}
            onSignIn={onSignIn}
            email={email}
            onEmailChange={setEmail}
            password={password}
            onPasswordChange={setPassword}
            decision={decision}
            onRegisterSuccess={onRegSuccess}
            inviteMode={!!inviteId}
            inviteId={inviteId}
            next={next}
            popmenuFlow={popmenuFlow}
            goBack={() => setStep(RegStep.Use)}
          />}
        {step === RegStep.Confirm &&
          <RegStepFour toDashboard={toDashboard} />}
        {step === RegStep.TrialSetup &&
          <RegStepTrial onNext={popmenuNext} />}
        {step === RegStep.PopmenuAuth &&
          <RegStepPopmenuAuth email={email} />}
        {step === RegStep.PopmenuLocation &&
          <RegStepPopmenuLocation onNext={popmenuNext} />}
      </div>
    </div>
  );
}
